export const useContentSourceRemap = (data: Data) => {
  const { content = null, watch = false, donation = false } = data;
  const remapedData = { watch, donation, content };

  return {
    watch: remapedData.watch,
    donation: remapedData.donation,
    content: {
      ...content,
      ...remapByType(content),
    },
  } as RemapedContent;
};

function remapByType(content: Content) {
  const sources = {
    previewSources: [],
    countdownSources: [],
    defaultSources: [],
    aesSources: [],
    fairplaySources: [],
  };

  if (content.contentType === 'Video') {
    sources.previewSources = Array.isArray(content.trailerLocatorUrl)
      ? content.trailerLocatorUrl ?? content.previewLocatorUrl
      : [content.trailerLocatorUrl ?? content.previewLocatorUrl];

    sources.countdownSources = Array.isArray(content.countdownStreamingUrl)
      ? content.countdownStreamingUrl
      : [content.countdownStreamingUrl];

    sources.defaultSources = Array.isArray(content.videoLocatorUrl)
      ? content.videoLocatorUrl
      : [content.videoLocatorUrl];

    sources.aesSources = Array.isArray(content.aesLocatorUrl)
      ? content.aesLocatorUrl
      : [content.aesLocatorUrl];

    sources.fairplaySources = Array.isArray(content.fairplayLocatorUrl)
      ? content.fairplayLocatorUrl
      : [content.fairplayLocatorUrl];
  } else {
    if (content.sourceType === 'live') {
      sources.previewSources = Array.isArray(content.previewStreamingUrl)
        ? content.previewStreamingUrl
        : [content.previewStreamingUrl];
      sources.countdownSources = Array.isArray(content.countdownStreamingUrl)
        ? content.countdownStreamingUrl
        : [content.countdownStreamingUrl];
      sources.defaultSources = Array.isArray(content.liveVideo)
        ? content.liveVideo
        : [content.liveVideo];
      sources.aesSources = Array.isArray(content.liveAES) ? content.liveAES : [content.liveAES];
      sources.fairplaySources = Array.isArray(content.liveFairplay)
        ? content.liveFairplay
        : [content.liveFairplay];
    } else {
      sources.previewSources = Array.isArray(content.previewStreamingUrl)
        ? content.previewStreamingUrl
        : [content.previewStreamingUrl];
      sources.countdownSources = Array.isArray(content.countdownStreamingUrl)
        ? content.countdownStreamingUrl
        : [content.countdownStreamingUrl || null];
      sources.defaultSources = Array.isArray(content.server) ? content.server : [content.server];
      sources.aesSources = Array.isArray(content.serverAES)
        ? content.serverAES
        : [content.serverAES];
      sources.fairplaySources = Array.isArray(content.serverFairplay)
        ? content.serverFairplay
        : [content.serverFairplay];
    }
  }

  return {
    previewSources: sources.previewSources.filter(Boolean),
    countdownSources: sources.countdownSources.filter(Boolean),
    defaultSources: sources.defaultSources.filter(Boolean),
    aesSources: sources.aesSources.filter(Boolean),
    fairplaySources: sources.fairplaySources.filter(Boolean),
  };
}

interface Data {
  watch: boolean;
  content: Content;
  token: null;
}

interface ResponseObj {
  watch: boolean;
  donation: boolean;
  content: Content & {
    previewSources: string[];
    countdownSources: string[];
    defaultSources: string[];
    aesSources: string[];
    fairplaySources: string[];
  };
}

interface RemapedContent {
  content: Content & {
    previewSources: string[];
    countdownSources: string[];
    defaultSources: string[];
    aesSources: string[];
    fairplaySources: string[];
  };
}

interface Content {
  _id: string;
  title: string;
  description: string;
  preview: string;
  bannerUrl: string;
  sourceType: string;
  timezone: string;
  date: string;
  time: string;
  duration: number;
  startAt: number;
  endAt: number;
  streamingStatus: number;
  video: unknown[];
  locators: string;
  creator: Creator;
  likesCount: number;
  views: number;
  watchedCount: number;
  onlyWithSubscription: boolean;
  exclusiveAssignment: boolean;
  author: string;
  featured: boolean;
  language: string;
  genres: unknown[];
  categories: unknown[];
  ageRating: number;
  countries: unknown[];
  tags: unknown[];
  actors: unknown[];
  actor: Actor;
  coins: number;
  coinsB: number;
  coinsC: number;
  videoType: string;
  paid: boolean;
  payPerView: boolean;
  payPerViewChannels: unknown[];
  verticalCover: string;
  horizontalCover: string;
  squareCoverName: string;
  previewTransparent: string;
  previewTransparentName: string;
  previewFirst: string;
  previewFirstName: string;
  previewSecond: string;
  previewThird: string;
  previewThirdName: string;
  posterTV: string;
  posterTVName: string;
  sliderTV: string;
  sliderTVName: string;
  previewVideo: string;
  previewStreamingUrl: string;
  onlineMobile: boolean;
  onlineTV: boolean;
  channelName: string;
  name: string;
  isAlternative: boolean;
  status: string;
  contentType: string;
  previewAsset: string;
  countdownVideoDuration: number;
  countdownLocator: CountdownLocator;
  private: boolean;
  merch: unknown[];
  showTV: boolean;
  createdByTvUser: boolean;
  donation: boolean;
  isShowViewsEnabled: boolean;
  askMeunknownthing: boolean;
  createdAt: Date;
  updatedAt: Date;
  watch: boolean;
  likedByUser: boolean;
  boughtByUser: boolean;
  remind: boolean;
  videoCount: number;
  detailImage: string;
  banner: string;
  previewVideoUrl: string;
  ceekers: boolean;
  meetingInfo: MeetingInfo;
  liveVideo?: string[];
  liveAES?: string | string[];
  liveFairplay?: string | string[];
  server?: unknown[];
  serverAES?: unknown[];
  serverFairplay?: unknown[];
  trailerLocatorUrl?: string | string[];
  previewLocatorUrl?: string | string[];
  videoLocatorUrl?: string | string[];
  countdownStreamingUrl?: string | string[];
  aesLocatorUrl?: string | string[];
  fairplayLocatorUrl?: string | string[];
}

interface Actor {
  avatar: string;
  mainName: string;
  name: string;
  id: string;
}

interface CountdownLocator {
  name: string;
  id: string;
  ism: string;
}

interface Creator {
  id: string;
  fullName: string;
  email: string;
}

interface Ivs {
  channel: Channel;
  streamKey: StreamKey;
}

interface Channel {
  arn: string;
  authorized: boolean;
  ingestEndpoint: string;
  insecureIngest: boolean;
  latencyMode: string;
  name: string;
  playbackRestrictionPolicyArn: string;
  playbackUrl: string;
  preset: string;
  recordingConfigurationArn: string;
  srt: Srt;
  type: string;
}

interface Srt {
  endpoint: string;
  passphrase: string;
}

interface StreamKey {
  arn: string;
  channelArn: string;
  value: string;
}

interface Room100MS {
  id: string;
  name: string;
  enabled: boolean;
  description: string;
  customer_id: string;
  app_id: string;
  recording_info: null;
  template_id: string;
  template: string;
  region: string;
  created_at: Date;
  updated_at: Date;
  customer: string;
  large_room: boolean;
}

interface MeetingInfo {
  inputUrl: string;
  sdkAuthToken: string;
  user: string;
  live?: boolean;
  playRecorded?: boolean;
}
